import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function DataIntelligencePage() {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-white pt-32 pb-20">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-blue-400/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold rounded-full mb-6">
                AI SOLUTIONS
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Data Intelligence
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Solutions
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Turn raw data into strategic insights. Unlock the full potential of your data with advanced analytics, visualization, and AI-powered intelligence.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="/contact" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Unlock Insights
                </a>
                <a href="/case-studies" className="px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg font-semibold hover:border-purple-500 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  View Results
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                CAPABILITIES
              </div>
              <h2 className="text-4xl font-bold mb-4">Our Data Services</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Comprehensive data intelligence solutions for informed decision-making
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-pie-chart-line',
                  title: 'Advanced Analytics',
                  description: 'Deep dive into your data with statistical analysis, trend identification, and pattern recognition.',
                  gradient: 'from-blue-500 to-cyan-500'
                },
                {
                  icon: 'ri-dashboard-line',
                  title: 'Data Visualization',
                  description: 'Interactive dashboards and reports that make complex data easy to understand.',
                  gradient: 'from-purple-500 to-pink-500'
                },
                {
                  icon: 'ri-database-line',
                  title: 'Data Warehousing',
                  description: 'Centralized data storage solutions for efficient access and management.',
                  gradient: 'from-indigo-500 to-blue-500'
                },
                {
                  icon: 'ri-lightbulb-line',
                  title: 'Predictive Insights',
                  description: 'Forecast future trends and outcomes using AI-powered predictive models.',
                  gradient: 'from-violet-500 to-purple-500'
                },
                {
                  icon: 'ri-flow-chart',
                  title: 'Data Pipeline',
                  description: 'Automated data collection, transformation, and integration workflows.',
                  gradient: 'from-blue-500 to-indigo-500'
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Data Governance',
                  description: 'Ensure data quality, security, and compliance across your organization.',
                  gradient: 'from-purple-500 to-violet-500'
                }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="group bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                >
                  <div className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${feature.gradient} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Data Journey */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                OUR APPROACH
              </div>
              <h2 className="text-4xl font-bold mb-4">From Data to Decisions</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                A systematic approach to transforming your data into actionable intelligence
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-6">
              {[
                {
                  step: '01',
                  title: 'Collect',
                  description: 'Gather data from all sources',
                  icon: 'ri-download-cloud-line'
                },
                {
                  step: '02',
                  title: 'Clean',
                  description: 'Process and validate data quality',
                  icon: 'ri-filter-line'
                },
                {
                  step: '03',
                  title: 'Analyze',
                  description: 'Extract patterns and insights',
                  icon: 'ri-search-eye-line'
                },
                {
                  step: '04',
                  title: 'Visualize',
                  description: 'Create intuitive dashboards',
                  icon: 'ri-bar-chart-grouped-line'
                },
                {
                  step: '05',
                  title: 'Act',
                  description: 'Make data-driven decisions',
                  icon: 'ri-rocket-2-line'
                }
              ].map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 text-center">
                    <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
                      {step.step}
                    </div>
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mx-auto mb-3">
                      <i className={`${step.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Analytics Types */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                ANALYTICS TYPES
              </div>
              <h2 className="text-4xl font-bold mb-4">Four Levels of Analytics</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                From understanding what happened to predicting what will happen
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  type: 'Descriptive Analytics',
                  icon: 'ri-file-list-line',
                  question: 'What happened?',
                  description: 'Understand past performance through historical data analysis and reporting.',
                  examples: ['Sales reports', 'Website traffic', 'Customer demographics']
                },
                {
                  type: 'Diagnostic Analytics',
                  icon: 'ri-search-line',
                  question: 'Why did it happen?',
                  description: 'Identify root causes and correlations behind past events and trends.',
                  examples: ['Churn analysis', 'Performance drivers', 'Anomaly investigation']
                },
                {
                  type: 'Predictive Analytics',
                  icon: 'ri-line-chart-line',
                  question: 'What will happen?',
                  description: 'Forecast future outcomes using statistical models and machine learning.',
                  examples: ['Demand forecasting', 'Risk assessment', 'Customer lifetime value']
                },
                {
                  type: 'Prescriptive Analytics',
                  icon: 'ri-lightbulb-flash-line',
                  question: 'What should we do?',
                  description: 'Recommend optimal actions based on data-driven insights and simulations.',
                  examples: ['Resource optimization', 'Pricing strategies', 'Marketing campaigns']
                }
              ].map((analytics, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg">
                      <i className={`${analytics.icon} text-xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{analytics.type}</h3>
                      <p className="text-sm text-gray-500 italic">{analytics.question}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4">{analytics.description}</p>
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-gray-600">Examples:</div>
                    <ul className="space-y-1">
                      {analytics.examples.map((example, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <div className="w-1 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">
              Transform Your Data Into
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Strategic Advantage
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Let's unlock the hidden value in your data and drive smarter business decisions.
            </p>
            <a 
              href="/contact"
              className="inline-block px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Start Your Data Journey
            </a>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}